import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@material-ui/core';
import Autocomplete from 'Components/DebouncedAutocomplete';
import { getLicenceTypeLabel, getUserDisplayName, isAssigned } from './assignmentModel';

const useStyles = makeStyles(theme => ({
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        paddingTop: theme.spacing(1),
    },
    hint: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.8125rem',
        lineHeight: 1.5,
    },
    fieldHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(0.75),
    },
    fieldLabel: {
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '0.8125rem',
        fontWeight: 700,
    },
    linkButton: {
        padding: theme.spacing(0.25, 1),
        minWidth: 0,
        textTransform: 'none',
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    option: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    optionText: {
        flex: 1,
        minWidth: 0,
        fontSize: '0.8125rem',
        overflowWrap: 'anywhere',
    },
    optionMeta: {
        flexShrink: 0,
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.6875rem',
        fontWeight: 600,
    },
    optionCheckbox: {
        padding: 4,
        color: 'var(--dm-muted, #718096)',
        backgroundColor: 'transparent !important',
        '&.Mui-checked': {
            color: theme.palette.primary.main,
        },
    },
    radio: {
        color: 'var(--dm-muted, #718096)',
        backgroundColor: 'transparent !important',
        '&.Mui-checked': {
            color: theme.palette.primary.main,
        },
    },
    summary: {
        padding: theme.spacing(1.25, 1.5),
        borderRadius: 8,
        backgroundColor: 'rgba(61, 125, 221, 0.08)',
        color: '#1f4f91',
        fontSize: '0.8125rem',
        lineHeight: 1.5,
    },
    warning: {
        padding: theme.spacing(1.25, 1.5),
        borderRadius: 8,
        backgroundColor: 'rgba(214, 158, 46, 0.12)',
        color: '#7b5a12',
        fontSize: '0.75rem',
        lineHeight: 1.5,
    },
}));

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const QuickAssignDialog = ({
    open,
    onClose,
    onApply,
    licences,
    users,
    draft,
    remainingByKey,
    initialUserIds,
}) => {
    const classes = useStyles();
    const [action, setAction] = useState('assign');
    const [selectedLicences, setSelectedLicences] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);

    useEffect(() => {
        if (!open) return;
        setAction('assign');
        setSelectedLicences([]);
        const preset = new Set(initialUserIds || []);
        setSelectedUsers(preset.size ? users.filter(user => preset.has(user.uuid)) : []);
        // Selection is seeded once per opening.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const assign = action === 'assign';

    const shortfalls = useMemo(() => {
        if (!assign) return [];
        return selectedLicences.map((licence) => {
            const needed = selectedUsers.filter(user => !isAssigned(draft, licence.key, user.uuid)).length;
            const remaining = Math.max(0, remainingByKey[licence.key] ?? licence.remaining);
            return { licence, needed, remaining };
        }).filter(item => item.needed > item.remaining);
    }, [assign, selectedLicences, selectedUsers, draft, remainingByKey]);

    const { changeCount, fittingCount } = useMemo(() => selectedLicences.reduce((totals, licence) => {
        const changes = selectedUsers.filter(user => isAssigned(draft, licence.key, user.uuid) !== assign).length;
        const remaining = Math.max(0, remainingByKey[licence.key] ?? licence.remaining);
        return {
            changeCount: totals.changeCount + changes,
            fittingCount: totals.fittingCount + (assign ? Math.min(changes, remaining) : changes),
        };
    }, { changeCount: 0, fittingCount: 0 }), [selectedLicences, selectedUsers, draft, assign, remainingByKey]);

    const handleApply = () => {
        onApply({
            keys: selectedLicences.map(licence => licence.key),
            userIds: selectedUsers.map(user => user.uuid),
            assign,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="quick-assign-title">
            <DialogTitle id="quick-assign-title">Quick assign</DialogTitle>
            <DialogContent className={classes.content}>
                <Typography className={classes.hint}>
                    Pick any number of licences and users. Changes are added to the matrix and saved when you click Save.
                </Typography>
                <RadioGroup row value={action} onChange={event => setAction(event.target.value)} aria-label="Action">
                    <FormControlLabel value="assign" control={<Radio color="default" className={classes.radio} />} label="Assign licences" />
                    <FormControlLabel value="remove" control={<Radio color="default" className={classes.radio} />} label="Remove licences" />
                </RadioGroup>

                <div>
                    <div className={classes.fieldHeader}>
                        <span className={classes.fieldLabel}>Licences</span>
                        <Button
                            size="small"
                            color="primary"
                            className={classes.linkButton}
                            onClick={() => setSelectedLicences(selectedLicences.length === licences.length ? [] : licences)}
                        >
                            {selectedLicences.length === licences.length && licences.length ? 'Clear' : `Select all ${licences.length}`}
                        </Button>
                    </div>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        limitTags={3}
                        size="small"
                        options={licences}
                        value={selectedLicences}
                        onChange={(event, value) => setSelectedLicences(value)}
                        groupBy={licence => getLicenceTypeLabel(licence.type, true)}
                        getOptionLabel={licence => licence.name}
                        getOptionSelected={(option, value) => option.key === value.key}
                        renderOption={(licence, { selected }) => (
                            <span className={classes.option}>
                                <Checkbox size="small" color="default" checked={selected} className={classes.optionCheckbox} />
                                <span className={classes.optionText}>{licence.name}</span>
                                <span className={classes.optionMeta}>
                                    {Math.max(0, remainingByKey[licence.key] ?? licence.remaining)} left
                                </span>
                            </span>
                        )}
                        renderInput={params => (
                            <TextField {...params} variant="outlined" placeholder={selectedLicences.length ? '' : 'Search licences'} />
                        )}
                    />
                </div>

                <div>
                    <div className={classes.fieldHeader}>
                        <span className={classes.fieldLabel}>Users</span>
                        <Button
                            size="small"
                            color="primary"
                            className={classes.linkButton}
                            onClick={() => setSelectedUsers(selectedUsers.length === users.length ? [] : users)}
                        >
                            {selectedUsers.length === users.length && users.length ? 'Clear' : `Select all ${users.length}`}
                        </Button>
                    </div>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        limitTags={3}
                        size="small"
                        options={users}
                        value={selectedUsers}
                        onChange={(event, value) => setSelectedUsers(value)}
                        getOptionLabel={user => [user.email, getUserDisplayName(user)].filter(Boolean).join(' - ')}
                        getOptionSelected={(option, value) => option.uuid === value.uuid}
                        renderTags={(value, getTagProps) => value.map((user, index) => (
                            <Chip key={user.uuid} size="small" label={user.email} {...getTagProps({ index })} />
                        ))}
                        renderOption={(user, { selected }) => (
                            <span className={classes.option}>
                                <Checkbox size="small" color="default" checked={selected} className={classes.optionCheckbox} />
                                <span className={classes.optionText}>{user.email}</span>
                                <span className={classes.optionMeta}>{getUserDisplayName(user)}</span>
                            </span>
                        )}
                        renderInput={params => (
                            <TextField {...params} variant="outlined" placeholder={selectedUsers.length ? '' : 'Search users'} />
                        )}
                    />
                </div>

                {selectedLicences.length > 0 && selectedUsers.length > 0 && (
                    <div className={classes.summary}>
                        {assign ? 'Assign' : 'Remove'} {plural(selectedLicences.length, 'licence')} {assign ? 'to' : 'from'} {plural(selectedUsers.length, 'user')}:
                        {' '}
                        {!changeCount && 'nothing to change.'}
                        {changeCount > 0 && fittingCount === changeCount && `${plural(changeCount, 'change')} will be added to the matrix.`}
                        {changeCount > 0 && fittingCount < changeCount && `${fittingCount} of ${plural(changeCount, 'requested change')} fit in the licences left and will be added to the matrix.`}
                    </div>
                )}
                {shortfalls.length > 0 && (
                    <div className={classes.warning}>
                        Not enough capacity for{' '}
                        {shortfalls.slice(0, 3).map(({ licence, needed, remaining }) => `${licence.name} (needs ${needed}, ${remaining} left)`).join('; ')}
                        {shortfalls.length > 3 ? ` and ${plural(shortfalls.length - 3, 'more licence')}` : ''}.
                        {' '}Users are filled in the order selected until the licence runs out.
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleApply}
                        disabled={!fittingCount}
                >
                    {assign ? 'Assign' : 'Remove'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QuickAssignDialog;
